import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, Loader2, LogOut, Phone, ShieldAlert } from "lucide-react";
import { usePartner } from "@/context/PartnerAuthContext";
import { useSettings } from "@/context/SettingsContext";
import { UPDATE_STATUS } from "@/lib/partnerApi";

export const P_CARD = "bg-white rounded-3xl border border-stone-200/80 p-5 sm:p-7 shadow-[0_1px_2px_rgba(24,53,47,0.04),0_16px_40px_-28px_rgba(24,53,47,0.18)]";
export const P_H1 = "font-display text-3xl sm:text-4xl font-bold text-[hsl(var(--forest))] leading-tight tracking-tight";
export const P_H2 = "font-display text-xl sm:text-2xl font-bold text-[hsl(var(--forest))] leading-tight";
export const P_EYEBROW = "font-mono text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--copper))]";
export const P_FIELD = "w-full px-4 py-3.5 border border-stone-300 rounded-2xl text-base text-stone-800 bg-white leading-relaxed focus:outline-none focus:border-[hsl(var(--copper))] transition-colors";
export const P_LABEL = "block text-sm font-semibold text-[hsl(var(--forest))] mb-2";
export const P_BTN = "inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 rounded-full bg-[hsl(var(--forest))] text-white text-base font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed";
export const P_BTN_COPPER = "inline-flex items-center justify-center gap-2 w-full px-7 py-4 rounded-full bg-[hsl(var(--copper))] text-white text-base font-semibold hover:opacity-90 transition-opacity disabled:opacity-40";
export const P_BTN_GHOST = "inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[48px] rounded-full border border-stone-300 bg-white text-stone-700 text-[15px] font-semibold hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition-colors disabled:opacity-40";

export function PartnerShell({ children }) {
    const { partner, logout } = usePartner();
    const unread = partner?.unread_notifications || 0;
    return (
        <div className="min-h-screen bg-[hsl(var(--cream))]" data-testid="partner-area">
            <header className="sticky top-0 z-30 border-b border-stone-200/60 bg-[hsl(var(--cream))]/85 backdrop-blur-md">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <Link to="/parceiros" className="flex items-center gap-2 min-w-0" data-testid="partner-home-link">
                        <span className="logo-mark text-2xl text-[hsl(var(--forest))]">H<span className="dot">.</span></span>
                        <span className="min-w-0">
                            <span className="block font-display font-bold text-sm text-[hsl(var(--forest))] leading-tight">O Homem da Obra</span>
                            <span className="block text-[11px] text-stone-500 leading-tight">Área de Parceiros</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-1">
                        <Link to="/parceiros/notificacoes" className="relative w-11 h-11 rounded-full flex items-center justify-center text-[hsl(var(--forest))] hover:bg-white transition-colors" aria-label="Notificações" data-testid="partner-notifications-link">
                            <Bell className="w-5 h-5" />
                            {unread > 0 && (
                                <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--copper))] text-white text-[10px] font-bold flex items-center justify-center" data-testid="partner-notifications-badge">{unread}</span>
                            )}
                        </Link>
                        <button type="button" onClick={logout} className="w-11 h-11 rounded-full flex items-center justify-center text-stone-500 hover:bg-white hover:text-stone-800 transition-colors" aria-label="Sair" data-testid="partner-logout">
                            <LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>
            <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16 sm:pt-10 space-y-6">{children}</main>
        </div>
    );
}

export function UrgentCard({ compact = false }) {
    const { settings } = useSettings();
    const phone = settings.phone || "";
    return (
        <div className={`rounded-3xl border border-red-200 bg-red-50 ${compact ? "p-4" : "p-5 sm:p-6"}`} data-testid="partner-urgent-card">
            <div className="flex items-start gap-3">
                <ShieldAlert className="w-6 h-6 text-red-700 flex-shrink-0" aria-hidden />
                <div className="min-w-0">
                    <div className="font-semibold text-red-900">Situação urgente?</div>
                    <p className="text-[15px] text-red-900/85 leading-relaxed mt-0.5">
                        Questões de segurança, danos ou impedimentos à execução devem ser comunicadas de imediato por telefone a O Homem da Obra. Depois, registe-as aqui.
                    </p>
                    {phone && (
                        <a href={`tel:${phone.replace(/\s/g, "")}`} className="mt-3 inline-flex items-center gap-2 min-h-[48px] px-5 rounded-full bg-red-700 text-white font-semibold hover:bg-red-800 transition-colors" data-testid="partner-urgent-call">
                            <Phone className="w-4 h-4" /> Ligar {phone}
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

export function BackLink({ to, label, testid = "partner-back" }) {
    return (
        <Link to={to} className="inline-flex items-center gap-2 min-h-[44px] text-[15px] font-semibold text-[hsl(var(--copper))] hover:opacity-80 transition-opacity" data-testid={testid}>
            <ArrowLeft className="w-4 h-4" aria-hidden /> {label}
        </Link>
    );
}

export function StatusPill({ status, testid }) {
    const s = UPDATE_STATUS[status] || UPDATE_STATUS.pending;
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${s.cls}`} data-testid={testid}>{s.label}</span>;
}

export function ObraCover({ src, className = "", testid }) {
    const [failed, setFailed] = useState(false);
    if (!src || failed) {
        return (
            <div className={`flex items-center justify-center bg-stone-100 ${className}`} data-testid={`${testid}-placeholder`}>
                <span className="logo-mark text-5xl text-stone-300" aria-hidden>H<span className="dot">.</span></span>
            </div>
        );
    }
    return <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className={`object-cover bg-stone-100 ${className}`} data-testid={testid} />;
}

export function PageLoading() {
    return (
        <div className="py-24 flex justify-center text-stone-400" data-testid="partner-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
        </div>
    );
}
